import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Upload } from 'lucide-react';
import { uploadParticipantDocument } from './participantDocumentsApi';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'ID', label: 'ID Document' },
  { value: 'SERVICE_AGREEMENT', label: 'Service Agreement' },
  { value: 'CONSENT', label: 'Consent Form' },
  { value: 'POLICY', label: 'Policy' },
  { value: 'INCIDENT_DOC', label: 'Incident Document' },
  { value: 'OTHER', label: 'Other' },
];

const SOURCE_CONTEXTS = [
  { value: 'manual', label: 'Manual Upload' },
  { value: 'shift', label: 'After Shift' },
  { value: 'appointment', label: 'After Appointment' },
  { value: 'visit', label: 'After Visit' },
  { value: 'incident', label: 'Incident Related' },
  { value: 'other', label: 'Other' },
];

export function ParticipantDocumentUploadModal({ participantId, open, onClose, onComplete, canManage, existingDocuments }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('OTHER');
  const [expiryDate, setExpiryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [sourceContext, setSourceContext] = useState('manual');
  const [contextNote, setContextNote] = useState('');
  const [staffCanDownload, setStaffCanDownload] = useState(false);
  const [replaceDocumentId, setReplaceDocumentId] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      if (!title) setTitle(f.name.replace(/\.[^.]+$/, ''));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title || file.name);
      formData.append('category', category);
      if (expiryDate) formData.append('expiryDate', expiryDate);
      if (notes) formData.append('notes', notes);
      formData.append('sourceContext', sourceContext);
      if (contextNote) formData.append('contextNote', contextNote);
      if (staffCanDownload) formData.append('staffCanDownload', 'true');
      if (replaceDocumentId && replaceDocumentId !== 'NONE') formData.append('replaceDocumentId', replaceDocumentId);

      await uploadParticipantDocument(participantId, formData);
      toast.success('Document uploaded successfully');
      onComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="upload-document-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-4 h-4" /> Upload Document
          </DialogTitle>
          <DialogDescription>Upload a document for this participant.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* File */}
          <div className="space-y-1">
            <Label className="text-xs font-medium">File *</Label>
            <Input
              type="file"
              onChange={handleFileChange}
              className="h-9 text-sm cursor-pointer"
              data-testid="upload-file-input"
            />
            {file && (
              <p className="text-[11px] text-muted-foreground">
                {file.name} ({file.size < 1048576 ? `${(file.size / 1024).toFixed(1)} KB` : `${(file.size / 1048576).toFixed(1)} MB`})
              </p>
            )}
          </div>

          {/* Title */}
          <div className="space-y-1">
            <Label className="text-xs font-medium">Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Document title (defaults to filename)"
              className="h-8 text-sm"
              data-testid="upload-title-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            {/* Category */}
            <div className="space-y-1">
              <Label className="text-xs font-medium">Document Type</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-8 text-sm" data-testid="upload-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Source Context */}
            <div className="space-y-1">
              <Label className="text-xs font-medium">Upload Context</Label>
              <Select value={sourceContext} onValueChange={setSourceContext}>
                <SelectTrigger className="h-8 text-sm" data-testid="upload-source-context">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_CONTEXTS.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Context Note — shown when context is not 'manual' */}
          {sourceContext !== 'manual' && (
            <div className="space-y-1">
              <Label className="text-xs font-medium">Context Note</Label>
              <Input
                value={contextNote}
                onChange={(e) => setContextNote(e.target.value)}
                placeholder={`e.g., Collected after ${sourceContext === 'appointment' ? 'GP visit' : sourceContext}...`}
                className="h-8 text-sm"
                data-testid="upload-context-note"
              />
            </div>
          )}

          {/* Replace Existing Document (version upgrade) — Admin/Coordinator only */}
          {canManage && existingDocuments && existingDocuments.length > 0 && (
            <div className="space-y-1">
              <Label className="text-xs font-medium">Replace Existing (New Version)</Label>
              <Select value={replaceDocumentId || 'NONE'} onValueChange={setReplaceDocumentId}>
                <SelectTrigger className="h-8 text-sm" data-testid="upload-replace-select">
                  <SelectValue placeholder="New document (no replacement)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">New document (no replacement)</SelectItem>
                  {existingDocuments.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.title}{d.version > 1 ? ` (v${d.version})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Expiry Date */}
          <div className="space-y-1">
            <Label className="text-xs font-medium">Expiry Date</Label>
            <Input
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              className="h-8 text-sm"
              data-testid="upload-expiry-input"
            />
          </div>

          {/* Notes */}
          <div className="space-y-1">
            <Label className="text-xs font-medium">Notes</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes..."
              className="h-8 text-sm"
              data-testid="upload-notes-input"
            />
          </div>

          {/* Staff Download Control — visible only to Admin/Coordinator */}
          {canManage && (
            <div className="flex items-center gap-2 pt-1" data-testid="upload-staff-download-control">
              <Checkbox
                id="staffCanDownload"
                checked={staffCanDownload}
                onCheckedChange={setStaffCanDownload}
                data-testid="upload-staff-download-checkbox"
              />
              <Label htmlFor="staffCanDownload" className="text-xs text-muted-foreground cursor-pointer">
                Allow support staff to download this document
              </Label>
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" size="sm" disabled={uploading || !file} data-testid="upload-submit-btn">
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
